import "./BasemapManager.css";
import { useAnalyticsHandler } from "hooks/useAnalytics";
import { useIsTouchDevice } from "hooks/useIsTouchDevice";
import { Text } from "components/typography";
import { cn } from "../util";
import lightImg from "../assets/light-img.png";
import darkImg from "../assets/dark-img.png";
import satelliteImg from "../assets/satellite-img.png";

export enum BasemapStyle {
  Satellite = "satellite",
  Light = "light",
  Dark = "dark",
}

export const BasemapMap: Record<BasemapStyle, string> = {
  [BasemapStyle.Satellite]:
    "https://api.maptiler.com/maps/0197e59d-418a-7810-a9c4-017e992114dd/style.json?key=kA23mbacz1UyORj2qBRz",
  [BasemapStyle.Light]:
    "https://api.maptiler.com/maps/0197229d-ea80-7c56-8410-8711d629e4b9/style.json?key=kA23mbacz1UyORj2qBRz",
  [BasemapStyle.Dark]:
    "https://api.maptiler.com/maps/base-v4-dark/style.json?key=kA23mbacz1UyORj2qBRz",
};

const basemapOptions = [
  {
    type: BasemapStyle.Satellite,
    name: "Satellite",
    image: satelliteImg,
  },
  {
    type: BasemapStyle.Light,
    name: "Light",
    image: lightImg,
  },
  {
    type: BasemapStyle.Dark,
    name: "Dark",
    image: darkImg,
  },
] as const;

const BasemapButton = ({
  selectedTheme,
  thisTheme,
  name,
  image,
  setTheme,
}: {
  selectedTheme: string;
  thisTheme: BasemapStyle;
  name: string;
  image: string;
  setTheme: (style: string) => void;
}) => {
  const isSelected = thisTheme === selectedTheme;
  const analyticsHandler = useAnalyticsHandler("Basemap Selected");
  const isLight = selectedTheme === BasemapStyle.Light;
  const borderColor = isLight
    ? "border-neutral-midfi-grey"
    : "border-neutral-off-white";
  const hoverBorderColor = isLight
    ? "hover:border-neutral-midfi-grey"
    : "hover:border-neutral-off-white";

  return (
    <button
      onClick={() => {
        setTheme(thisTheme);
        analyticsHandler({
          basemap: thisTheme,
        });
      }}
      className="flex flex-col items-center gap-1"
    >
      <div
        className={cn(
          "overflow-hidden rounded-lg border-[1px]",
          hoverBorderColor,
          isSelected
            ? `border-[3px] ${borderColor}`
            : `m-[2px] border-transparent`,
        )}
      >
        <img src={image} alt={name} className="h-10 w-10 object-cover" />
      </div>
      <Text
        level={4}
        className={cn(
          "text-xs uppercase",
          isSelected ? "font-bold" : "font-normal",
          isLight
            ? "basemap-selector-text-halo text-neutral-midfi-grey"
            : "text-neutral-off-white",
        )}
      >
        {name}
      </Text>
    </button>
  );
};

export const BasemapManager = ({
  theme,
  setTheme,
}: {
  theme: string;
  setTheme: (style: string) => void;
}) => {
  const isTouchDevice = useIsTouchDevice();

  return (
    <div
      className={cn(
        "absolute flex justify-end gap-2 pt-4",
        isTouchDevice ? "flex-col" : "flex-row",
        "right-[10px] bottom-[20px]",
      )}
      data-cy="basemap-manager"
    >
      {basemapOptions.map((option) => (
        <BasemapButton
          key={option.type}
          selectedTheme={theme}
          thisTheme={option.type}
          name={option.name}
          image={option.image}
          setTheme={setTheme}
        />
      ))}
    </div>
  );
};
